import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import moment from 'moment';

import { ContextProviderProps } from 'types/contextProviderProps';
import { UserInterface } from 'interfaces/userInterface';
import { UserContext } from 'context/user';
import UserAccount from 'interfaces/users/userAccount';
import { StorageConstants } from 'constants/storageConstants';
import Utilities from 'api/lib/Utilities';
import { SavedPatientSearchData } from 'types/patientAccount';
import { SavedCoreMeasurePatientSearchData } from 'types/CoreMeasurePatient';
import { FacilityUserSearchState } from 'types/facilityUserAccount';
import { SelectedReportFilters } from 'types/ReportGlobalFilters';

const UserProvider = (props: ContextProviderProps): React.JSX.Element => {
  const storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear'> = typeof window !== 'undefined'
    ? window.sessionStorage
    : {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
      clear: () => undefined,
    };

  const [events] = useState(['click', 'load', 'scroll', 'keypress', 'keydown']);
  const [currentUser, setCurrentUser] = useState<UserInterface | null>(null);
  const [currentAccount, setCurrentAccount] = useState<UserAccount | null>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showSystemAlerts, setShowSystemAlerts] = useState<boolean>(true);
  const [patientReflectionsConsentAccepted, setPatientReflectionsConsentAccepted] = useState<boolean>(false);
  const [patientHipaaConsentAccepted, setPatientHipaaConsentAccepted] = useState<boolean>(false);
  const [activeGroupTherapySessionId, setActiveGroupTherapySessionId] = useState<string | null>(storage.getItem(StorageConstants.ActiveGroupTherapySessionId));
  const [activeGroupTherapyPatientId, setActiveGroupTherapyPatientId] = useState<string | null>(storage.getItem(StorageConstants.ActiveGroupTherapyPatientId));
  const [activeTabletModeId, setActiveTabletModeId] = useState<string | null>(storage.getItem(StorageConstants.ActiveTabletModeId));
  const [activeTabletModePatientId, setActiveTabletModePatientId] = useState<string | null>(storage.getItem(StorageConstants.ActiveTabletModePatientId));
  const [activeTabletModeAssessments, setActiveTabletModeAssessmnets] = useState<string | null>(storage.getItem(StorageConstants.ActiveTabletModeAssessments));
  const { instance } = useMsal();

  const currentUserRef = useRef<UserInterface | null>(undefined);
  const checkTimerIntervalRef = useRef<any>(undefined);
  const isAuthenticatedRef = useRef<boolean>(false);
  isAuthenticatedRef.current = useIsAuthenticated();

  const [triggerLogout, setTriggerLogout] = useState<boolean>(false);

  const [patientSearchData, setPatientSearchData] = useState<SavedPatientSearchData | null>(null);
  const [coreMeasuresPatientSearchData, setCoreMeasuresPatientSearchData] = useState<SavedCoreMeasurePatientSearchData | null>(null);
  const [selectedReportFilters, setSelectedReportFilters] = useState<SelectedReportFilters | null>(null);
  const [selectedReportCategory, setSelectedReportCategory] = useState<number>(0);
  const [facilityUserSearchQuery, setFacilityUserSearchQuery] = useState<FacilityUserSearchState | null>(null);
  const [isNavExpanded, setIsNavExpanded] = useState<boolean>(false);
  const [isCoreMeasuresExpanded, setIsCoreMeasuresExpanded] = useState<boolean>(false);
  const [isReferencesExpanded, setIsReferencesExpanded] = useState<boolean>(false);
  const [reportTrackingID, setReportTrackingID] = useState<string>('');
  const [workBookHash, setWorkBookHash] = useState<string>('');

  const LOGOUT_TIME_LIMIT = 20; // 20 minutes
  const WARNING_TIME_LIMIT = 15; // 15 minutes

  /**
   * App logout callback.
   */
  const azureLogout = (): void => {
    setTriggerLogout(true);
    // setCurrentUser(null);
    // clearTimeout(checkTimerIntervalRef.current);
    // storage.removeItem(StorageConstants.Timestamp);
    // storage.removeItem(StorageConstants.MsalExpiration);
  };

  /**
   * Check the timer to see if it exceeded the warning/logout timer.
   */
  const checkForExpirationTime = (): void => {
    const timestampStr = storage.getItem(StorageConstants.Timestamp);
    const exp = storage.getItem(StorageConstants.MsalExpiration);
    if (timestampStr && exp) {
      const timestamp = Number.parseInt(timestampStr, 10);
      const diff = moment.duration(moment().diff(moment(timestamp)));
      const expTimestamp = new Date(0).setUTCSeconds(Number.parseInt(exp, 10));

      if (timestamp >= expTimestamp) {
        azureLogout();
      }

      if (diff.minutes() >= LOGOUT_TIME_LIMIT) {
        azureLogout();
      } else if (diff.minutes() >= WARNING_TIME_LIMIT) {
        setShowPopup(true);
      }
    }
  };

  /**
   * When one of the listed event is triggered, update the last activity time stamp and reset
   * time timeout timers.
   */
  const resetTimer = useCallback(() => {
    if (isAuthenticatedRef.current) {
      storage.setItem(StorageConstants.Timestamp, String(moment().valueOf()));
      setShowPopup(false);

      if (currentUser) {
        checkForExpirationTime();
      }
    } else {
      clearTimeout(checkTimerIntervalRef.current);
      storage.removeItem(StorageConstants.Timestamp);
    }
  }, [isAuthenticatedRef.current]);

  /**
   * Login event initialization.
   */
  const setupLoginEvent = (newUser: UserInterface): void => {
    setCurrentUser(newUser);
    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Check if the previous timestamp already exists.  If it exists, check to see if it's expired.
    if (storage.getItem(StorageConstants.Timestamp)) {
      checkForExpirationTime();
    } else {
      storage.setItem(StorageConstants.Timestamp, String(moment().valueOf()));
    }

    checkTimerIntervalRef.current = setTimeout(function timeoutFnc() {
      checkForExpirationTime();
      checkTimerIntervalRef.current = setTimeout(timeoutFnc, 5000);
    }, 5000);
  };

  const DismissAlerts = (): void => {
    setShowSystemAlerts(false);
  };

  useEffect(() => {
    // If the user auth is set, but then current user is cleared, then it's in the process of logging out.
    const userLogoutAuth = storage.getItem(StorageConstants.UserAuthority);

    // if (triggerLogout && userLogoutAuth && currentUser === null) {
    if (triggerLogout && userLogoutAuth) {
      const logoutRequest: any = {
        onRedirectNavigate: () => {
          storage.clear();
          setCurrentUser(null);
          clearTimeout(checkTimerIntervalRef.current);
          storage.removeItem(StorageConstants.Timestamp);
          storage.removeItem(StorageConstants.MsalExpiration);
          return true;
        },
      };

      const msalHomeAccountID = storage.getItem(StorageConstants.MsalHomeAccountID) || '';
      const userAccount = instance.getAccountByHomeId(msalHomeAccountID);
      const appProvider = storage.getItem(StorageConstants.MsalAppProvider);

      // AD logout differs from B2C logout.  Checking the appProvider seems like the best choice, unless it's proven
      // to be unreliable.
      if (userAccount && Utilities.stringAreEqual(appProvider, 'REF-ADD')) {
        logoutRequest.account = userAccount;
      } else {
        logoutRequest.authority = userLogoutAuth;
      }
      instance.logoutRedirect(logoutRequest);
    }
  }, [currentUser, triggerLogout]);

  useEffect(() => {
    currentUserRef.current = currentUser;
    if (currentUser) {
      storage.setItem(StorageConstants.UserAuthority, currentUser.authority);
    }
  }, [currentUser]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!window.onfocus) {
      window.onfocus = checkForExpirationTime;
    }
  }, [checkForExpirationTime]);

  const changeActiveGroupTherapySessionId = (groupId: string | null): void => {
    if (groupId === null) {
      storage.removeItem(StorageConstants.ActiveGroupTherapySessionId);
    } else {
      storage.setItem(StorageConstants.ActiveGroupTherapySessionId, groupId);
    }

    setActiveGroupTherapySessionId(groupId);
  };

  const changeActiveGroupTherapyPatientId = (patientId: string | null): void => {
    if (patientId === null) {
      storage.removeItem(StorageConstants.ActiveGroupTherapyPatientId);
    } else {
      storage.setItem(StorageConstants.ActiveGroupTherapyPatientId, patientId);
    }

    setActiveGroupTherapyPatientId(patientId);
  };

  const changeActiveTabletModeId = (tabletModeId: string | null): void => {
    if (tabletModeId == null) {
      storage.removeItem(StorageConstants.ActiveTabletModeId);
    } else {
      storage.setItem(StorageConstants.ActiveTabletModeId, tabletModeId);
    }

    setActiveTabletModeId(tabletModeId);
  };

  const changeActiveTabletModePatientId = (patientId: string | null): void => {
    if (patientId === null) {
      storage.removeItem(StorageConstants.ActiveTabletModePatientId);
    } else {
      storage.setItem(StorageConstants.ActiveTabletModePatientId, patientId);
    }

    setActiveTabletModePatientId(patientId);
  };

  const changeActiveTabletModeAssessments = (assessmentsJson: string | null): void => {
    if (assessmentsJson == null) {
      storage.removeItem(StorageConstants.ActiveTabletModeAssessments);
    } else {
      storage.setItem(StorageConstants.ActiveTabletModeAssessments, assessmentsJson);
    }

    setActiveTabletModeAssessmnets(assessmentsJson);
  };

  return (
    <UserContext.Provider
      value={{
        user: currentUser,
        userAccount: currentAccount,
        isPopupActive: showPopup,
        isSystemAlertsActive: showSystemAlerts,
        activeGroupTherapySessionId,
        activeGroupTherapyPatientId,
        activeTabletModeId,
        activeTabletModePatientId,
        activeTabletModeAssessments,
        patientReflectionsConsentAccepted,
        patientHipaaConsentAccepted,
        patientSearchData,
        coreMeasuresPatientSearchData,
        selectedReportFilters,
        selectedReportCategory,
        facilityUserSearchQuery,
        isNavExpanded,
        isCoreMeasuresExpanded,
        isReferencesExpanded,
        reportTrackingID,
        workBookHash,
        SetPatientSearchData: setPatientSearchData,
        SetCoreMeasuresPatientSearchData: setCoreMeasuresPatientSearchData,
        SetSelectedReportFilters: setSelectedReportFilters,
        SetSelectedReportCategory: setSelectedReportCategory,
        SetFacilityUserSearchQuery: setFacilityUserSearchQuery,
        SetPatientReflectionsConsentAccepted: setPatientReflectionsConsentAccepted,
        SetPatientHipaaConsentAccepted: setPatientHipaaConsentAccepted,
        SetActiveGroupTherapySessionId: changeActiveGroupTherapySessionId,
        SetActiveGroupTherapyPatientId: changeActiveGroupTherapyPatientId,
        SetActiveTabletModeId: changeActiveTabletModeId,
        SetActiveTabletModePatientId: changeActiveTabletModePatientId,
        SetActiveTabletModeAssessments: changeActiveTabletModeAssessments,
        DismissSystemAlerts: DismissAlerts,
        SetIsNavExpanded: setIsNavExpanded,
        SetIsCoreMeasuresExpanded: setIsCoreMeasuresExpanded,
        SetIsReferencesExpanded: setIsReferencesExpanded,
        SetReportTrackingID: setReportTrackingID,
        SetWorkBookHash: setWorkBookHash,
        Logout: () => azureLogout(),
        Login: (newUser: UserInterface) => setupLoginEvent(newUser),
        SetAccount: (newAccount: UserAccount) => setCurrentAccount(newAccount),
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export default UserProvider;
